import { useCallback } from "react";
import { getLogger } from "@/Common/helpers/getLogger";
import { usePersistentSelection } from "@/Common/hooks/selection/usePersistentSelection";
import { useInventoryContext } from "./contexts/InventoryFilterContext";
import { PropertyPaths } from "@/Common/helpers/typeHelpers";
import { get } from "lodash";
import { InventoryTab } from "./constants";
import { Checkbox, CheckboxProps } from "@mui/material";
import { useSelectionContext } from "@/Common/context/selection/SelectionContext";

const log = getLogger("SelectionCheckbox");

export type OnChangeCallback<T> = (checked: boolean, id: string, row: T) => void;

interface SelectionAndFilterCheckboxProps<T = unknown> extends CheckboxProps {
	_id: string;
	table: string;
	onChangeCallback?: OnChangeCallback<T>;
	pathToVinNumber: PropertyPaths<T, string>;
	row: T;
	origin: InventoryTab;
	isSingleSelect?: boolean;
	disableFilter?: boolean;
	commissionCycleId?: string;
	payPeriodId?: string;
}

export const SelectionAndFilterCheckbox = <T,>(props: Readonly<SelectionAndFilterCheckboxProps<T>>) => {
	const {
		table,
		onChangeCallback,
		_id,
		row,
		pathToVinNumber,
		origin,
		isSingleSelect = false,
		disableFilter = false,
		commissionCycleId,
		payPeriodId,
		...checkboxProps
	} = props;

	const { addToSelection } = useSelectionContext();
	const {
		addToFilter,
		removeFromFilter,
		commissionCycleId: contextCommissionCycleId,
		payPeriodId: contextPayPeriodId,
	} = useInventoryContext();

	const usedCommissionCycleId = commissionCycleId ?? contextCommissionCycleId;
	const usedPayPeriodId = payPeriodId ?? contextPayPeriodId;

	const { selectedIds } = usePersistentSelection({
		table,
		commissionCycleId: usedCommissionCycleId,
		payPeriodId: usedPayPeriodId,
	});

	const handleChangeCheckbox = useCallback<(_e: unknown, checked: boolean) => void>(
		(_e, checked) => {
			log("Handling checkbox change '%s' %o", _id, row, isSingleSelect);
			const pathArray = pathToVinNumber.split(".");
			const vinNumber: string = get(row, pathArray);
			const filterItem = {
				table,
				_id,
				origin,
				vinNumber,
				commissionCycleId: usedCommissionCycleId,
				payPeriodId: usedPayPeriodId,
			};
			onChangeCallback?.(checked, _id, row);
			if (checked && !disableFilter) {
				addToFilter(filterItem, isSingleSelect);
			} else if (checked && disableFilter) {
				addToSelection(filterItem, isSingleSelect);
			} else {
				removeFromFilter(filterItem);
			}
		},
		[table, onChangeCallback, usedCommissionCycleId, usedPayPeriodId]
	);

	return <Checkbox {...checkboxProps} checked={selectedIds.includes(_id)} onChange={handleChangeCheckbox} />;
};
